import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../hooks/useTypedSelector";
import { TruckModel } from "../../domain/models/TruckModel";
import useCrudForm from "../../../../../hooks/useCrudForm";
import {
  driverSchema,
  truckFormSchema,
} from "../../domain/schema/truckFormSchema";
import { TruckMutateFormTemplate } from "../features/truckMutateFormTemplate";
import { Breadcrumb } from "../../../../general/modules/commons/breadcrumb/breadcrumb";
import {
  Layout,
  LayoutContent,
  LayoutHeader,
  LayoutHeaderTitle,
} from "../../../../general/modules/commons/layout/layout";
import { Form } from "react-bootstrap";
import { logout } from "../../../../auth/infrastructure/slices/authSlice/authSlice";
import { Content } from "../../../../general/modules/commons/content/content";
import { ContentHeader } from "../../../../general/modules/commons/content/contentHeader";
import { BreadcrumbItem } from "react-bootstrap";
import { EntitySelectField } from "../../../../general/fields/modules/commons/entitySelectField/entitySelectField";
import { useEffect, useState } from "react";
import useCrudFilters from "../../../../../hooks/useCrudFilters";
import { LicencePlateField } from "../../../../general/fields/modules/commons/licencePlateField/licencePlateField";
import LicensePlate from "license-plate";

export const TruckMutatePage = () => {
  const { t } = useTranslation("common");
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => state.auth.user);

  const model = useAppSelector((state) => state.crudMutate).model as TruckModel;

  const [driverId, setDriverId] = useState<string>();
  const [licenceValue, setLicenceValue] = useState<string>();
  const [licenceString, setLicenceString] = useState<string>();

  useEffect(() => {
    if (model.driver) {
      setDriverId(`${model.driver.id}`);
    }
    if (model.licence) {
      const licence = new LicensePlate(model.licence);
      setLicenceValue(licence.pretty());
      setLicenceString(model.licence);
    }
  }, [model]);

  const title = model.id
    ? t("truck.mutate.edit", { name: model.title })
    : t("truck.mutate.create");

  const userFilters = useCrudFilters({
    searchBy: "SearchByRootUser",
    fields: {
      roles: {
        fields: {
          name: {
            in: ["dbh-driver", "dbh-employee", "dbh-manager"],
          },
        },
      },
    },
  });

  const crud = useCrudForm({
    schema: truckFormSchema,
    prefix: "trucks",
    createMutation: "upsertTruck",
    updateMutation: "upsertTruck",
    deleteMutation: "deleteTruck",
    id: id ? parseInt(id) : undefined,
    editUrl: "/truck/:id/edit",
    formTemplate: TruckMutateFormTemplate,
    mutationExclude: ["driver"],
    queryExclude: ["user_id"],
    onBeforeSubmit: (values) => {
      return {
        ...values,
        id: id ? parseInt(id) : undefined,
        user_id: driverId,
        licence: licenceString?.toUpperCase(),
      };
    },
  });

  if (Object.keys(model).length < 1 && id) return <></>;

  if (!currentUser || currentUser.role === "dealer-employee") {
    return (
      <Layout>
        <LayoutContent>
          <Content>
            <ContentHeader className={"mt-3"} title={t("Access denied")} />
            <p className={"mt-3"}>{t("You don't have access to this page")}</p>
          </Content>
        </LayoutContent>
      </Layout>
    );
  }

  return (
    <Layout
      header={
        <LayoutHeader logout={() => void dispatch(logout())}>
          <LayoutHeaderTitle title={title} />
          <Breadcrumb>
            <BreadcrumbItem href="/trucks">{t("Trucks")}</BreadcrumbItem>
            <BreadcrumbItem>{title}</BreadcrumbItem>
          </Breadcrumb>
        </LayoutHeader>
      }
    >
      <LayoutContent>
        <Content>
          <ContentHeader className={"mb-3"} title={title} />
          <Form.Group className="mb-3">
            <Form.Label>{t("Licence plate")}</Form.Label>
            <LicencePlateField
              id={"licence"}
              key={"licence"}
              name={"licence"}
              value={licenceValue ?? ""}
              onChange={(event) => {
                if (event.target.value.replace(/-/g, "").length > 6) return;
                const plate = new LicensePlate(event.target.value);
                if (plate.valid()) {
                  setLicenceValue(plate.pretty());
                  setLicenceString(event.target.value);
                } else {
                  setLicenceValue(event.target.value);
                  setLicenceString(event.target.value);
                }
              }}
            />
          </Form.Group>

          {crud.form}

          <Form.Group className="mb-3">
            <Form.Label>{t("Driver")}</Form.Label>
            <EntitySelectField
              filters={userFilters.filters}
              prefix="users"
              entity="User"
              labelPrefix={"name"}
              id={"users.id"}
              key={"users.id"}
              name={"users.id"}
              value={driverId == undefined ? `${model?.driver?.id}` : driverId}
              onChange={(option) => {
                if (option === null) return;
                setDriverId((option as { value: string }).value);
              }}
              schema={driverSchema}
              limit={1000000}
            />
          </Form.Group>
        </Content>

        <Content md={4}>
          {crud.manageHeader}
          {crud.manage}
        </Content>
      </LayoutContent>
    </Layout>
  );
};
